import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  UtensilsCrossed, Heart, History, RotateCcw, CreditCard, Clock3, BellRing, Sparkles, ArrowRight, ZoomIn, X,
} from "lucide-react";
import { useLocalization } from "../../hooks/useLocalization";
import { PublicHeader } from "../../components/PublicHeader";
import { Dialog, DialogContent } from "../../components/ui/dialog";

const FEATURE_ICONS = [UtensilsCrossed, Sparkles, Heart, History, RotateCcw, CreditCard, Clock3, BellRing];
const MENUS = [
  {
    key: "wings",
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_yoko-payroll-system/artifacts/z82r3ulu_image.webp",
    alt: "YOKOLICIOUS full wings and chicken menu with pricing",
  },
  {
    key: "breakfast",
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_yoko-payroll-system/artifacts/tr5yj8qh_image.png",
    alt: "YOKOLICIOUS breakfast burrito menu with pricing",
  },
];

export default function HomePage() {
  const { t, setLanguage } = useLocalization();
  const navigate = useNavigate();
  const { lang } = useParams();
  const [menuZoomed, setMenuZoomed] = useState(null);
  const [activeMenu, setActiveMenu] = useState("wings");

  useEffect(() => {
    if (lang === "en" || lang === "ja") setLanguage(lang);
  }, [lang, setLanguage]);

  const features = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => t(`homepage.feature${n}`));

  return (
    <div className="min-h-screen bg-white text-blue-950" data-testid="public-homepage">
      <PublicHeader />

      {/* Hero - a contained, framed panel rather than a full-bleed background photo, so the
          page reads as a clean, professional storefront rather than a giant poster. */}
      <section className="bg-white relative overflow-hidden border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-14 items-center">
          <div className="max-w-xl">
            <span
              data-testid="homepage-coming-soon-badge"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400 text-blue-950 text-xs font-bold tracking-wide uppercase mb-6"
            >
              <Sparkles size={14} />
              {t("homepage.comingSoon")}
            </span>
            <h1
              data-testid="homepage-banner-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-5 text-blue-950"
            >
              {t("homepage.bannerTitle")}
            </h1>
            <p data-testid="homepage-banner-subtitle" className="text-sm sm:text-base text-gray-500 max-w-lg leading-relaxed mb-8">
              {t("homepage.bannerSubtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#homepage-menu"
                data-testid="homepage-coming-soon-cta"
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-blue-950 font-bold hover:bg-amber-300 hover:-translate-y-0.5 active:scale-95 transition-all shadow-lg shadow-amber-400/20"
              >
                {t("homepage.viewMenuCta")}
                <ArrowRight size={16} />
              </a>
              <button
                data-testid="homepage-staff-login-secondary"
                onClick={() => navigate("/careers")}
                className="flex items-center gap-2 px-6 py-3 rounded-full border border-gray-300 text-blue-950 font-semibold hover:bg-gray-50 active:scale-95 transition-all"
              >
                {t("nav.careers")}
              </button>
            </div>
          </div>

          {/* Contained, rounded storefront photo card - elegant frame instead of an edge-to-edge background image. */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl shadow-gray-200 border border-gray-200 aspect-[4/3] lg:aspect-[5/4]">
              <img
                src="/images/yokolicious-storefront.jpg"
                alt="YOKOLICIOUS storefront at night"
                className="w-full h-full object-cover"
              />
            </div>
            <img
              src="/yokolicious-eagle-salute.png"
              alt="YOKOLICIOUS saluting eagle mascot in captain's uniform"
              data-testid="homepage-eagle-mascot"
              className="hidden sm:block absolute -bottom-6 -right-6 h-28 sm:h-32 object-contain drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="homepage-menu" className="bg-white py-16 sm:py-24" data-testid="homepage-menu-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-8 sm:mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-bold uppercase tracking-wide mb-4">
              <UtensilsCrossed size={13} /> {t("homepage.menuBadge")}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-3">{t("homepage.menuTitle")}</h2>
            <p className="text-sm sm:text-base text-gray-500">{t("homepage.menuSubtitle")}</p>
          </div>

          {/* Two menus, presented as an elegant tab switcher rather than stacking both posters. */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-gray-100 mb-6" data-testid="homepage-menu-tabs">
            <button
              data-testid="homepage-menu-tab-wings"
              onClick={() => setActiveMenu("wings")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${activeMenu === "wings" ? "bg-blue-950 text-white shadow" : "text-gray-500 hover:text-blue-950"}`}
            >
              {t("homepage.menuTabWings")}
            </button>
            <button
              data-testid="homepage-menu-tab-breakfast"
              onClick={() => setActiveMenu("breakfast")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${activeMenu === "breakfast" ? "bg-blue-950 text-white shadow" : "text-gray-500 hover:text-blue-950"}`}
            >
              {t("homepage.menuTabBreakfast")}
            </button>
          </div>

          {MENUS.filter((m) => m.key === activeMenu).map((menu) => (
            <button
              key={menu.key}
              data-testid={`homepage-menu-zoom-trigger-${menu.key}`}
              onClick={() => setMenuZoomed(menu)}
              className="group relative block w-full rounded-2xl overflow-hidden border border-gray-200 shadow-lg hover:shadow-xl transition-shadow animate-in fade-in duration-300"
            >
              <img src={menu.image} alt={menu.alt} className="w-full h-auto" />
              <span className="absolute inset-0 bg-blue-950/0 group-hover:bg-blue-950/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <span className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-blue-950 text-sm font-bold shadow-lg">
                  <ZoomIn size={16} /> {t("homepage.menuZoomCta")}
                </span>
              </span>
            </button>
          ))}
          <p className="text-xs text-gray-500 mt-3 text-center sm:text-left">{t("homepage.menuFootnote")}</p>
        </div>
      </section>

      <Dialog open={!!menuZoomed} onOpenChange={(open) => !open && setMenuZoomed(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-transparent border-none shadow-none" data-testid="homepage-menu-zoom-dialog">
          <button
            data-testid="homepage-menu-zoom-close"
            onClick={() => setMenuZoomed(null)}
            className="absolute -top-3 -right-3 z-10 bg-white text-blue-950 rounded-full p-2 shadow-lg hover:bg-gray-100"
          >
            <X size={18} />
          </button>
          {menuZoomed && (
            <img src={menuZoomed.image} alt={menuZoomed.alt} className="w-full h-auto rounded-xl max-h-[90vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>

      {/* Features */}
      <section className="relative bg-white text-blue-950 py-16 sm:py-24 border-t border-gray-100" data-testid="homepage-features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-3">{t("homepage.featuresTitle")}</h2>
            <p className="text-sm sm:text-base text-gray-500">{t("homepage.featuresSubtitle")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((label, idx) => {
              const Icon = FEATURE_ICONS[idx];
              return (
                <div
                  key={idx}
                  data-testid={`homepage-feature-${idx}`}
                  className="relative rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                    {t("homepage.upcomingBadge")}
                  </span>
                  <div className="w-11 h-11 rounded-xl bg-blue-950 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-amber-400" />
                  </div>
                  <p className="text-sm font-semibold leading-snug">{label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Store info + footer */}
      <footer className="bg-white border-t border-gray-100 py-14" data-testid="homepage-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
          <div>
            <h3 className="text-xl font-bold mb-1 text-blue-950" data-testid="homepage-store-info-title">{t("homepage.storeInfoTitle")}</h3>
            <p className="text-sm text-gray-500" data-testid="homepage-store-info-subtitle">{t("homepage.storeInfoSubtitle")}</p>
          </div>
          <p className="text-xs text-gray-500 max-w-md" data-testid="homepage-footer-note">{t("homepage.footerNote")}</p>
        </div>
      </footer>
    </div>
  );
}
